import React, { createContext, useState, useEffect, useContext } from 'react';
import { fetchProducts } from '../services/productService';
import { fetchCollections } from '../services/collectionService';

const ShopContext = createContext();

export const useShop = () => useContext(ShopContext);

export const ShopProvider = ({ children }) => {
    const [products, setProducts] = useState([]);
    const [collections, setCollections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [error, setError] = useState(null);

    // Helper to merge products with collection stock data
    const mergeProductsWithCollections = (productsData, collectionsData) => {
        if (!productsData || !collectionsData) return productsData;

        return productsData.map(product => {
            if (!product.collectionId) return product;

            const collection = collectionsData.find(c => c._id === product.collectionId);
            if (!collection) return product;

            // NEW: Inject Hex Codes from Collection
            if (product.colors && product.colors.length > 0 && collection.colors && collection.colors.length > 0) {
                product.colors.forEach(productColor => {
                    // Try to find matching color in collection by name
                    const collectionColor = collection.colors.find(c => c.name === productColor.label);
                    if (collectionColor && collectionColor.code) {
                        productColor.hex = collectionColor.code;
                    }
                });
            }

            const outOfStockColors = [];
            let isOutOfStock = false;

            // Check stock logic
            if (product.colors && product.colors.length > 0 && collection.isColorIndexed && collection.stockCombinations) {
                product.colors.forEach(productColor => {
                    if (productColor.id === 'default') return;
                    // Check if ANY combination with this color has stock > 0
                    const hasStock = collection.stockCombinations.some(combo => {
                        return combo.color === productColor.label && combo.quantity > 0;
                    });
                    if (!hasStock) {
                        outOfStockColors.push(productColor.id);
                    }
                });

                // For color-indexed, check if ALL colors are out of stock
                // If there are NO combinations with quantity > 0, then completely out of stock
                isOutOfStock = !collection.stockCombinations.some(comb => comb.quantity > 0);
            } else if (!collection.isColorIndexed && collection.stockCombinations) {
                // Non-indexed
                const totalStock = collection.stockCombinations.reduce((acc, curr) => acc + (curr.quantity || 0), 0);
                isOutOfStock = totalStock === 0;
            } else if (!collection.stockCombinations || collection.stockCombinations.length === 0) {
                isOutOfStock = true;
            }

            return {
                ...product,
                collection,
                outOfStockColors,
                isOutOfStock
            };
        });
    };

    const loadData = React.useCallback(async (isBackgroundRefresh = false) => {
        if (!isBackgroundRefresh) setLoading(true);
        else setIsRefreshing(true);

        try {
            console.log('Fetching shop data...');

            // Fetch both in parallel
            const [productsRes, collectionsRes] = await Promise.all([
                fetchProducts(), // Fetch ALL products (no filters)
                fetchCollections()
            ]);

            if (productsRes.success && collectionsRes.success) {
                const activeCollections = collectionsRes.data.filter(col => col.status !== 'archived');
                const hydratedProducts = mergeProductsWithCollections(productsRes.data, activeCollections);

                setProducts(hydratedProducts);
                setCollections(activeCollections);

                // Update cache
                localStorage.setItem('shop_products', JSON.stringify(hydratedProducts));
                localStorage.setItem('shop_collections', JSON.stringify(activeCollections));
                localStorage.setItem('shop_last_updated', Date.now().toString());

                console.log('Shop data updated and cached.');
            } else {
                const errorMsg = productsRes.error || collectionsRes.error || 'Failed to fetch shop data';
                console.error(errorMsg);
                if (!isBackgroundRefresh) setError(errorMsg);
            }
        } catch (err) {
            console.error('Error loading shop data:', err);
            if (!isBackgroundRefresh) setError(err.message);
        } finally {
            if (!isBackgroundRefresh) setLoading(false);
            else setIsRefreshing(false);
        }
    }, []);

    useEffect(() => {
        // 1. Try to load from cache first
        const cachedProducts = localStorage.getItem('shop_products');
        const cachedCollections = localStorage.getItem('shop_collections');

        if (cachedProducts && cachedCollections) {
            try {
                setProducts(JSON.parse(cachedProducts));
                setCollections(JSON.parse(cachedCollections));
                setLoading(false); // Valid data available immediately
                console.log('Loaded shop data from local cache.');
            } catch (e) {
                console.error('Failed to parse cached shop data', e);
            }
        }

        // 2. Always fetch fresh data in background (Stale-While-Revalidate)
        loadData(!!(cachedProducts && cachedCollections));
    }, [loadData]);

    const refreshShop = React.useCallback(() => loadData(true), [loadData]);

    const value = React.useMemo(() => ({
        products,
        collections,
        loading,
        isRefreshing,
        error,
        refreshShop
    }), [products, collections, loading, isRefreshing, error, refreshShop]);

    return (
        <ShopContext.Provider value={value}>
            {children}
        </ShopContext.Provider>
    );
};
